import { Button, Divider, Stack, Typography } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useAppFlags } from '$launch-darkly/app-flags';
import { GlossaryButton } from '../components/GlossaryButton/GlossaryButton';
import { useReportHeaderActionLayout } from '../hooks/useReportHeaderActionLayout';
import { ReportContainer } from '../ReportContainer/ReportContainer';
import { TransactionsTable } from '../TransactionsTable';
import { AIInsightsButton } from './components/AIInsightsButton';
import { ComparisonView } from './components/ComparisonView';
import { FilterButtons } from './components/FilterButtons';
import { GeographyPerformanceTable } from './components/GeographyPerformanceTable';
import { LeadSourcePerformanceTable } from './components/LeadSourcePerformanceTable';
import { PerformanceOverTimeChart } from './components/PerformanceOverTimeChart';
import { PerformanceReportFilters } from './components/PerformanceReportFilters';
import { PerformanceSnapshot } from './components/PerformanceSnapshot';
import { TeamPerformanceTable } from './components/TeamPerformanceTable';
import {
  PERFORMANCE_REPORT_CONTEXT_DEFAULTS,
  usePerformanceReportContext,
} from './context/PerformanceReportContext';
import { useGoalsAvailable } from './hooks/useGoalsAvailable';

// Module-level so the prop keeps a stable reference across renders; the
// glossary memoizes on it.
const GOALS_GLOSSARY_CATEGORY = ['Goals'];

export function PerformanceReport() {
  const router = useRouter();
  const { viewReportAs, embedded } = router.query;
  const { data: session } = useSession();
  const { aiReportInsightButton, __loading__: flagsLoading } = useAppFlags();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const viewingAs = viewReportAs?.toString() ?? session?.user?.id;
  const isEmbedded = embedded === 'true';
  const { isCompactActions, isNarrowActions, isSmallScreen } =
    useReportHeaderActionLayout();
  const { filterCount, updateContext } = usePerformanceReportContext();
  const showAIInsightsButton = Boolean(!flagsLoading && aiReportInsightButton);
  const { goalsAvailable } = useGoalsAvailable({ viewReportAs: viewingAs });
  const glossaryHiddenCategories = goalsAvailable
    ? undefined
    : GOALS_GLOSSARY_CATEGORY;

  if (!viewingAs) return null;

  return (
    <ReportContainer
      viewReportAs={viewingAs}
      title="Performance"
      subtitle="Explore trends and uncover insights about sales over time."
      boldedSubtitle="Note that Leases and Referrals are excluded from this view."
      isEmbedded={isEmbedded}
      glossaryHiddenCategories={glossaryHiddenCategories}
      extra={
        <FilterButtons
          id="performance-report-filters-button"
          size="medium"
          fillAvailableSpace={isSmallScreen}
          onFilterButtonClick={() => {
            setIsFiltersOpen(true);
          }}
          extraButton={
            <Stack
              direction="row"
              sx={{
                gap: { xs: 1, md: 1.25, lg: 2 },
                flexShrink: 0,
              }}
            >
              {showAIInsightsButton ? (
                <AIInsightsButton
                  viewReportAs={viewingAs}
                  showOnlyIcon={isCompactActions}
                  size="medium"
                />
              ) : null}
              <GlossaryButton
                showOnlyIcon={isCompactActions}
                size="medium"
                hiddenCategories={glossaryHiddenCategories}
              />
            </Stack>
          }
        />
      }
      forceNarrowActionLayout={isNarrowActions}
      scrollingHeaderExtra={
        <>
          <FilterButtons
            onFilterButtonClick={() => {
              setIsFiltersOpen(true);
            }}
            size="small"
            extraButton={
              filterCount > 0 ? (
                <Button
                  variant="link"
                  size="small"
                  onClick={() =>
                    updateContext({
                      filters: PERFORMANCE_REPORT_CONTEXT_DEFAULTS.filters,
                    })
                  }
                >
                  Clear All
                </Button>
              ) : null
            }
          />
          {showAIInsightsButton ? (
            <AIInsightsButton
              id="ai-insights-sticky-button"
              viewReportAs={viewingAs}
              showOnlyIcon={isCompactActions}
              size="small"
            />
          ) : null}
        </>
      }
    >
      <PerformanceReportFilters
        open={isFiltersOpen}
        onClose={() => {
          setIsFiltersOpen(false);
        }}
        viewingAs={viewingAs}
      />
      <Stack sx={{ gap: { xs: 6, md: 4.5 } }}>
        <Stack
          sx={{
            gap: { xs: 6, md: 4.5 },
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { lg: 'stretch' },
          }}
        >
          <PerformanceSnapshot
            viewReportAs={viewingAs}
            sx={{ flex: { lg: '1 1 0' }, minWidth: { lg: 0 } }}
          />
          <ComparisonView
            viewReportAs={viewingAs}
            sx={{ flex: { lg: '1 1 0' }, minWidth: { lg: 0 } }}
          />
        </Stack>
        <PerformanceOverTimeChart viewReportAs={viewingAs} />
        <Divider />
        <TeamPerformanceTable viewReportAs={viewingAs} />
        <Divider />
        <Stack sx={{ gap: 3 }} id="sources-and-markets-section">
          <Typography variant="h3">Sources & Markets</Typography>
          <LeadSourcePerformanceTable viewReportAs={viewingAs} />
          <GeographyPerformanceTable viewReportAs={viewingAs} />
        </Stack>
        <Divider />
        <TransactionsTable viewReportAs={viewingAs} />
      </Stack>
    </ReportContainer>
  );
}
